import _, {findIndex, findLastIndex, isEmpty, isEqual, isInteger, isNull, isUndefined, without} from 'lodash';
import React, {ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {globalWindow} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {isInputLike} from '../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {
  hasMetaKey,
  isBeforeAction,
  KeyboardActionsEnum,
} from '../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useEventHandler} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {FrontClickEvent} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  AsyncMoreStatusesEnum,
  AsyncStatusesEnum,
} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  LoaderRendererOptions,
  ScrollDirectionsEnum,
  ScrollerMoreStatuses,
} from '../../../../../libs/shared/core/src/helpers/types/scrollerTypes';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {isTeamNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {usePush} from '../../../../../libs/shared/core/src/services/push/pushContext';
import {CellsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {KeyboardActionEventHandler} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {Loader} from '../../../../../libs/shared/ui/src/components/loaders/loader';
import {
  VirtualScroller,
  VirtualScrollerScrollContext,
} from '../../../../../libs/shared/ui/src/components/scroll/virtualScroller';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {ItemIndexes} from '../../../../../srcCommon/helpers/itemIndexHelpers';
import {
  startPerformanceInteraction,
  startSimplePerformanceInteraction,
} from '../../../../../srcCommon/services/performanceMetrics/interactionPerformance';
import {PerformanceInteractionTypesEnum} from '../../../../../srcCommon/services/performanceMetrics/interactionPerformanceTypes';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getSelectionOnClick, getSelectionOnKey} from '../../../../helpers/selectionHelpers';
import {ReselectDirectionsEnum} from '../../../../models/api/preferencesModel';
import {viewerFetchPerformanceId} from '../../../../services/performanceMetrics/performanceIdHelpers';
import {clearHiddenConversationIdAction} from '../../../../store/data/conversations/conversationsActions';
import {getIsUserVisitor} from '../../../../store/data/teammates/teammatesSelectors';
import {
  CellsPanelConversationsPageState,
  CellsPanelPageState,
  stateIsCellsPanelPageConversationState,
} from '../../../../store/panels/cells/cellsPanelState';
import {computeSearchHighlightLength} from '../../../../store/panels/cells/helpers/cellsPanelPathHelpers';
import {getCellsSearchQueryHash} from '../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {
  hasSearchInInboxesParameters,
  inboxSupportsRefinements,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  allExceptInboxesSelection,
  computeIsAllExceptSelection,
  computeIsEmptyInboxesSelection,
  computeIsEqualSelection,
  computeIsIdSelected,
  emptyInboxesSelection,
  findFirstSelectedId,
  findOnlySelectedId,
  InboxesSelection,
  normalInboxesSelection,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {trackSearchConversationCellClick} from '../../search/searchTrackingHelpers';
import {
  GoOnlineBanner,
  GoOnlineBannerRenderLocationsEnum,
} from '../../viewer/activity/offline/goOnlineBanner';
import {CellsOfflineIndicator} from '../cellsOfflineIndicator';
import {CellsRefinementOptionsContainer} from '../refinement/cellsRefinementOptionsContainer';
import {CellsSortOptionsContainer} from '../sort/cellsSortOptionsContainer';
import {CellsEmptyContainer} from './cellsEmpty/cellsEmptyContainer';
import {CellsLoader} from './cellsLoader';
import {ItemId} from './cellsTypes';

/*
 * Constants.
 */

const cellHeight = 92;
const compactCellHeight = 38;
const loaderHeight = 40;

/*
 * Props.
 */

export type CellsItemsRequestMoreAsyncHandler = (arg: ScrollDirectionsEnum) => Promise<void>;
export type CellsItemsClickEventHandler<T extends ItemId> = (
  itemId: T,
  event: FrontClickEvent<HTMLElement>,
) => void;

type CellsPanelPageStateOf<T extends ItemId> = CellsPanelPageState & {
  parameters: {
    selection: InboxesSelection<T>;
  };
};

export type CellsItemsChildrenRenderer = (cells: ReactNode, options: CellsItemsChildrenOptions) => ReactNode;
export interface CellsItemsChildrenOptions {
  isEmpty: boolean;
  isCompact: boolean;
  isOffline: boolean;
}

interface CellsItemsProps<T extends ItemId> {
  panelId: CellsPanelId;
  userId: TeammateModel['id'];
  hasNextPage: boolean;
  page: CellsPanelPageStateOf<T>;
  reselectDirection: ReselectDirectionsEnum;
  itemIds: ReadonlyArray<T>;
  itemIndexes: ItemIndexes;
  focusedId: T | undefined;
  renderItem: (id: T, onClick: CellsItemsClickEventHandler<T>) => ReactNode;
  defaultId: T;
  hiddenConversationId?: T | null;
  children: CellsItemsChildrenRenderer;
  onSelectionChange: (arg: InboxesSelection<T>) => void;
  onRetryClick: () => void;
  onRequestMoreAsync: CellsItemsRequestMoreAsyncHandler;
  isCompact: boolean;
  isOffline: boolean;
  cellHeight?: number;
  shouldNotIncludeTopPadding?: boolean;
}

/*
 * Style.
 */

const StyledCellsDiv = styled.div`
  grid-row: 3;
  grid-column: 1;
`;

const StyledOptionWrapperDiv = styled.div<{
  $hasTopBoundary?: boolean;
  $isCompact: boolean;
  $shouldIncludeTopPadding: boolean;
}>`
  grid-row: 2;
  grid-column: 1;

  ${(p) =>
    p.$hasTopBoundary &&
    css`
      z-index: 1;
      box-shadow: 0 7px 0 ${p.theme.appBackground};
    `}

  display: flex;
  align-items: center;
  justify-content: space-between;
  ${(p) => p.$shouldIncludeTopPadding && `padding-top: 14px;`}
  padding-bottom: ${(p) => (p.$isCompact ? 5 : 2)}px;
  overflow: auto;
`;

const SpacerDiv = styled.div`
  flex-grow: 1;
`;

const StyledLoaderDiv = styled.div`
  height: ${loaderHeight}px;
  ${center()};
`;

/*
 * Component.
 */

export const CellsItems = <T extends ItemId>(props: CellsItemsProps<T>) => {
  const {page, itemIds, itemIndexes, hiddenConversationId, userId, isCompact, shouldNotIncludeTopPadding} =
    props;

  const overscanCalculator = useCallback(
    (viewportHeight: number, itemHeight: number) => Math.floor(viewportHeight / itemHeight) * 0.5,
    [],
  );

  const {parameters} = page;
  const {selection} = parameters;
  const {isConnected} = usePush();

  const isUserVisitor = useAppSelector(getIsUserVisitor);

  const lastValidSelectionRef = useRef<InboxesSelection<ItemId>>(emptyInboxesSelection);
  const itemIdToSelectNextRef = useRef<T>();
  const wasLastKeyDirectionBeforeRef = useRef(false);
  const previousItemIndexes = usePrevious(itemIndexes);
  const previousHiddenConversationId = usePrevious(hiddenConversationId);
  const previousIndexOfHiddenConversationIdRef = useRef<number | null>(-1);
  const [hasTopScrollBoundary, setHasTopScrollBoundary] = useState(false);

  const allItemIds = itemIds;

  const filteredItemIds = useMemo(
    () => filterHiddenConversationIdFromItemIds(itemIds, hiddenConversationId),
    [itemIds, hiddenConversationId],
  );

  useEffect(() => {
    // If the hidden conversation ID didn't change, nothing to do.
    if (previousHiddenConversationId === props.hiddenConversationId) {
      return;
    }

    // Conversation got hidden, let's store its current index for later.
    if (
      previousHiddenConversationId === null &&
      isInteger(props.hiddenConversationId) &&
      props.hiddenConversationId &&
      previousItemIndexes
    ) {
      previousIndexOfHiddenConversationIdRef.current = previousItemIndexes[props.hiddenConversationId];
    }
    // Hidden conversation got removed, let's reset its index.
    else if (
      isInteger(previousHiddenConversationId) &&
      props.hiddenConversationId === null &&
      isInteger(previousIndexOfHiddenConversationIdRef.current)
    ) {
      previousIndexOfHiddenConversationIdRef.current = null;
    }
  });

  function updateItemToSelectNext() {
    if (page.status !== AsyncStatusesEnum.LOADED) {
      return;
    }

    itemIdToSelectNextRef.current = findItemToSelectNext(
      props,
      filteredItemIds,
      itemIdToSelectNextRef.current,
      wasLastKeyDirectionBeforeRef.current,
      previousHiddenConversationId,
      previousIndexOfHiddenConversationIdRef.current,
    );
  }

  // TODO: Parameterize the getCellsSearch* selectors on the panelId and/or get this information passed down.
  const queryHash = useAppSelector(getCellsSearchQueryHash);
  const highlightLength = useAppSelector((state) => computeSearchHighlightLength(state, props.isCompact));

  function maybeUpdateLastValidSelectedIds() {
    if (page.status !== AsyncStatusesEnum.LOADED) {
      return;
    }

    // Check if this is a valid selection, and if it's different from the last.
    const isValidSelection = computeIsValidSelection(selection, itemIndexes);
    if (!isValidSelection || computeIsEqualSelection(lastValidSelectionRef.current, selection)) {
      return;
    }

    lastValidSelectionRef.current = selection;

    const selectedId = findOnlySelectedId<ItemId>(selection);
    if (
      !isUndefined(selectedId) && // Only track a single element.
      stateIsCellsPanelPageConversationState(page) && // Only track conversations.
      hasSearchInInboxesParameters(parameters) && // Only track when searching.
      page.parameters.search?.query === parameters.search.query && // Only track if query from page and parameters are equal.
      !isUndefined(queryHash) // Only track if queryHash is defined.
    ) {
      trackCellSelection(selectedId, page, queryHash, highlightLength, allItemIds);
    }
  }

  const previousPageUid = usePrevious(page.uid);
  useEffect(() => {
    // If we have a new page, clear the previously calculated selection info.
    if (isNull(previousPageUid) || previousPageUid !== page.uid) {
      lastValidSelectionRef.current = emptyInboxesSelection;
      itemIdToSelectNextRef.current = undefined;
    }

    maybeUpdateLastValidSelectedIds();
    updateItemToSelectNext();
  });

  const {onSelectionChange} = props;

  useEffect(() => {
    // We don't want to redirect if selected conversation is hidden (to avoid removing it from P3).
    const shouldNotRedirect = isHiddenConversationSelected(selection, hiddenConversationId);

    if (shouldNotRedirect) {
      return;
    }

    const nextSelection = findNextSelection(
      props,
      allItemIds,
      lastValidSelectionRef.current,
      itemIdToSelectNextRef.current,
    );

    if (!nextSelection) {
      return;
    }

    onSelectionChange(nextSelection);
  });

  /*
   * Event handlers.
   */

  const dispatch = useAppDispatch();
  const clearHiddenConversation = useCallback(() => {
    dispatch(clearHiddenConversationIdAction());
  }, [dispatch]);

  const {focusedId} = props;

  const onCellClick: CellsItemsClickEventHandler<T> = useEventHandler((itemId, event) => {
    // Give up if something inside the cell (such as the read/unread dot) has already handled the event.
    if (event.defaultPrevented) {
      return;
    }

    wasLastKeyDirectionBeforeRef.current = false;
    // We use a timeout to avoid a cascading render.
    globalWindow.setInstrumentedTimeout(clearHiddenConversation, 0);

    maybeLogViewerFetchInteraction(event, selection, itemId);

    // Prevent the default behavior.
    event.preventDefault();

    // Find the new IDs to select and replace the current location.
    const newSelection = getSelectionOnClick(selection, allItemIds, itemIndexes, itemId, event);
    onSelectionChange(newSelection);
  });

  const onBeforeOrAfterAction: KeyboardActionEventHandler = useCallback(
    (event, actionEvent) => {
      // Don't handle the event if we're in some kind of input field.
      if (isInputLike(event.target)) {
        return;
      }

      const {action} = actionEvent;

      wasLastKeyDirectionBeforeRef.current = isBeforeAction(action);

      // Don't allow scrolling with the arrows.
      event.preventDefault();

      // Find the new IDs to select.
      const isBefore = isBeforeAction(action);
      let newSelection = getSelectionOnKey(selection, allItemIds, itemIndexes, event, isBefore);

      // If the potential hidden conversation is selected, we have to check whether this conversation is the focused one.
      // In that case, we should compute if this conversation is located at the top or the bottom.
      if (
        isHiddenConversationSelected(selection, hiddenConversationId) &&
        focusedId &&
        focusedId === hiddenConversationId
      ) {
        const selectedId = hiddenConversationId;
        const hasPreviousIndex = isInteger(previousIndexOfHiddenConversationIdRef.current);

        // We want to use the index the hidden conversation used to have and not its current one.
        const selectedIdIndex = hasPreviousIndex
          ? previousIndexOfHiddenConversationIdRef.current
          : itemIndexes[selectedId];
        const isSelectedIdFirst = selectedIdIndex === 0;
        const isSelectedIdLast = selectedIdIndex === allItemIds.length - 1;

        // We may need to override the index to make the up/down arrows work like expected.
        const overridenPreviousIndex = computePreviousOverridenIndex(
          isSelectedIdFirst,
          isSelectedIdLast,
          allItemIds,
          previousIndexOfHiddenConversationIdRef.current,
          isBefore,
        );
        const overrideIndexes = hasPreviousIndex
          ? {
              ...itemIndexes,
              [selectedId]: overridenPreviousIndex,
            }
          : itemIndexes;
        // We need to remove the hidden conversation from our array to avoid issues caused by two items having the same index.
        const cleanedAllItemIds = allItemIds.filter((id) => id !== hiddenConversationId);

        // If our hidden+selected conversation is first of the list, then the next id will always be the 2nd one, regardless of `isBefore`
        if (isSelectedIdFirst) {
          newSelection = getSelectionOnKey(selection, cleanedAllItemIds, overrideIndexes, event, false);
        }
        // If our hidden+selected conversation is last of the list, then the next id will always be the penultimate one, regarless of `isBefore`.
        else if (isSelectedIdLast) {
          newSelection = getSelectionOnKey(selection, cleanedAllItemIds, overrideIndexes, event, true);
        }
        // If our hidden+selected conversation is in the middle then carry on.
        else if (isInteger(previousIndexOfHiddenConversationIdRef.current)) {
          newSelection = getSelectionOnKey(selection, cleanedAllItemIds, overrideIndexes, event, isBefore);
        }
      }

      if (isEqual(newSelection, selection)) {
        return;
      }

      // Navigate.
      onSelectionChange(newSelection);

      // We use a timeout to avoid a cascading render.
      globalWindow.setInstrumentedTimeout(clearHiddenConversation, 0);
    },
    [
      selection,
      allItemIds,
      itemIndexes,
      hiddenConversationId,
      focusedId,
      onSelectionChange,
      clearHiddenConversation,
    ],
  );

  const onSelectAllAction: KeyboardActionEventHandler = useCallback(
    (event) => {
      // Don't let the browser select text.
      event.preventDefault();

      const newSelection = allExceptInboxesSelection([]);

      if (isEqual(newSelection, selection)) {
        return;
      }

      // Navigate.
      onSelectionChange(newSelection);
    },
    [selection, onSelectionChange],
  );

  /*
   * Scroller.
   */

  const {renderItem} = props;
  const renderItemForId = useCallback((id: T) => renderItem(id, onCellClick), [renderItem, onCellClick]);
  const renderLoaderForOptions = useCallback(
    (options: LoaderRendererOptions) => renderCellsItemsBaseLoader(options, !isConnected),
    [isConnected],
  );

  const onScroll: (context: VirtualScrollerScrollContext) => void = ({event, scrollTop}) => {
    startSimplePerformanceInteraction(event, PerformanceInteractionTypesEnum.CELLS_SCROLL);

    setHasTopScrollBoundary(shouldShowTopScrollBoundary(scrollTop));
  };

  const virtualScrollerRef = useRecheckTopScrollBoundary(setHasTopScrollBoundary);

  /*
   * Infinite load.
   */

  const {moreStatusTop, moreStatusBottom} = page;
  const moreStatuses: ScrollerMoreStatuses = useMemo(
    () => ({
      [ScrollDirectionsEnum.TOP]: moreStatusTop,
      [ScrollDirectionsEnum.BOTTOM]: moreStatusBottom,
    }),
    [moreStatusTop, moreStatusBottom],
  );

  const {onRequestMoreAsync} = props;
  const onLoadMore: (arg: ScrollDirectionsEnum) => void = useCallback(
    async (direction) => {
      try {
        await onRequestMoreAsync(direction);
      } catch (error) {
        reportToBugsnag(error);
      }
    },
    [onRequestMoreAsync],
  );

  /*
   * Render.
   */

  const keyHandlers = {
    [KeyboardActionsEnum.SELECT_ALL]: onSelectAllAction,
    [KeyboardActionsEnum.MOVE_UP]: onBeforeOrAfterAction,
    [KeyboardActionsEnum.MOVE_DOWN]: onBeforeOrAfterAction,
    [KeyboardActionsEnum.SELECT_UP]: onBeforeOrAfterAction,
    [KeyboardActionsEnum.SELECT_DOWN]: onBeforeOrAfterAction,
  };

  const isCellsEmpty =
    allItemIds.length === 0 &&
    moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED &&
    moreStatusBottom === AsyncMoreStatusesEnum.ALL_LOADED;

  // If we're not loaded yet, display a loader or an error.
  if (page.status !== AsyncStatusesEnum.LOADED) {
    // Hack to disable P2 filters (refinements) in delegated inboxes, because this feature is unfinished and relativley low value.
    const refinementsIsDelegatedWorkspace = page.cellsContext.teammateId !== userId;

    return (
      <>
        {props.children(
          <CellsLoader
            status={page.status}
            onRetryClick={props.onRetryClick}
            inboxSupportsRefinements={
              !refinementsIsDelegatedWorkspace && inboxSupportsRefinements(page.parameters)
            }
            hasSearchInInboxesParameters={hasSearchInInboxesParameters(page.parameters)}
          />,
          {
            isEmpty: false,
            isCompact,
            isOffline: props.isOffline,
          },
        )}
      </>
    );
  }

  // If we're empty OR if we only have a hidden conversation, no need to display the list.
  if (
    filteredItemIds.length === 0 &&
    moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED &&
    moreStatusBottom === AsyncMoreStatusesEnum.ALL_LOADED
  ) {
    return (
      <>
        {props.children(
          <CellsEmptyContainer cellsContext={page.cellsContext} isOffline={props.isOffline} />,
          {
            isEmpty: isCellsEmpty,
            isCompact,
            isOffline: props.isOffline,
          },
        )}
      </>
    );
  }

  const defaultBehaviorCellHeight = isCompact ? compactCellHeight : cellHeight;
  const itemHeight = props.cellHeight || defaultBehaviorCellHeight;
  const virtualScrollerBaseProps = {
    focusedId,
    itemHeight,
    renderItem: renderItemForId,
    moreStatuses,
    loaderHeight,
    renderLoader: renderLoaderForOptions,
    onLoadMore,
    onScroll,
  };

  // Hack to disable P2 filters (refinements) in delegated inboxes, because this feature is unfinished and relativley low value.
  const refinementsIsDelegatedWorkspace = page.cellsContext.teammateId !== userId;

  // TODO: Refactor the P2 filtering/sorting component styles/layout. It's impossible to change without breaking it.
  return (
    <>
      {props.children(
        <>
          <StyledOptionWrapperDiv
            $hasTopBoundary={hasTopScrollBoundary}
            $isCompact={isCompact}
            $shouldIncludeTopPadding={!shouldNotIncludeTopPadding}
          >
            {!isUserVisitor && !refinementsIsDelegatedWorkspace && inboxSupportsRefinements(parameters) && (
              <CellsRefinementOptionsContainer page={page} parameters={parameters} />
            )}
            {!hasSearchInInboxesParameters(parameters) ? (
              <>
                <SpacerDiv />
                <CellsOfflineIndicator isOffline={props.isOffline} />
              </>
            ) : null}
            <CellsSortOptionsContainer
              inboxSupportsRefinements={
                !refinementsIsDelegatedWorkspace && inboxSupportsRefinements(parameters)
              }
              page={page}
              parameters={parameters}
            />
          </StyledOptionWrapperDiv>
          <KeyboardShortcuts handlers={keyHandlers} shouldAllowRepeats noPreventDefault>
            <StyledCellsDiv role="listbox">
              <VirtualScroller<T>
                {...virtualScrollerBaseProps}
                virtualScrollerRef={virtualScrollerRef}
                itemIds={filteredItemIds}
                overscan={isCompact ? overscanCalculator : 0}
                padding={{
                  top: 0,
                  bottom: 18,
                }}
              />
            </StyledCellsDiv>
          </KeyboardShortcuts>
        </>,
        {isEmpty: false, isCompact, isOffline: props.isOffline},
      )}
    </>
  );
};

export function renderCellsItemsBaseLoader(
  {moreStatus, direction}: LoaderRendererOptions,
  showOfflineUI: boolean,
) {
  if (moreStatus === AsyncMoreStatusesEnum.HAS_MORE) {
    return (
      <StyledLoaderDiv>
        <Loader size={VisualSizesEnum.MEDIUM} isEnabled={false} />
      </StyledLoaderDiv>
    );
  }

  if (moreStatus === AsyncMoreStatusesEnum.LOADING) {
    return (
      <StyledLoaderDiv>
        <Loader size={VisualSizesEnum.MEDIUM} />
      </StyledLoaderDiv>
    );
  }

  if (
    showOfflineUI &&
    moreStatus === AsyncMoreStatusesEnum.ERROR &&
    direction === ScrollDirectionsEnum.BOTTOM
  ) {
    return <GoOnlineBanner renderLocation={GoOnlineBannerRenderLocationsEnum.P2} />;
  }

  return null;
}

/*
 * Helpers.
 */

function shouldShowTopScrollBoundary(scrollTop: number): boolean {
  return scrollTop >= 10;
}

function useRecheckTopScrollBoundary(setHasTopScrollBoundary: (value: boolean) => void) {
  const virtualScrollerRef = useRef<HTMLDivElement | null>(null);
  // Intentionally called without a dependency array to check the scroll position on
  // each render. Make sure this function is lightweight and does not run the risk of
  // causing cascading rerenders.
  useLayoutEffect(() => {
    if (!virtualScrollerRef.current) {
      return;
    }

    setHasTopScrollBoundary(shouldShowTopScrollBoundary(virtualScrollerRef.current.scrollTop));
  });
  return virtualScrollerRef;
}

function computeIsValidSelection(routeSelection: InboxesSelection<ItemId>, itemIndexes: ItemIndexes) {
  // An "all except" selection is valid as long as P2 is not empty.
  return computeIsAllExceptSelection(routeSelection)
    ? !isEmpty(itemIndexes)
    : routeSelection.selectedIds.some((id) => Number.isInteger(itemIndexes[id.toString()]));
}

function findNextSelection<T extends ItemId>(
  props: CellsItemsProps<T>,
  allItemIds: ReadonlyArray<T>,
  lastValidSelection: InboxesSelection<ItemId>,
  itemIdToSelectNext: T | undefined,
): InboxesSelection<T> | undefined {
  // Extract our props of interest.
  const {hasNextPage, page, defaultId, itemIndexes} = props;
  const {parameters, status} = page;
  const {selection} = parameters;

  const isLastValid = computeIsValidSelection(lastValidSelection, itemIndexes);
  const selectedIndexes = calculateSelectedIndexes(itemIndexes, allItemIds, selection);

  // Check if there's a need to redirect.
  if (
    isLastValid || // The last valid selection is still valid.
    selectedIndexes.length > 0 || // An item is selected.
    hasNextPage || // There's already a next page loading with new parameters.
    status !== AsyncStatusesEnum.LOADED // The page is not loaded.
  ) {
    return undefined;
  }

  // If there's no item selected in the route, ignore the ID to select next.
  const idToSelectNext = !computeIsEmptyInboxesSelection(selection) ? itemIdToSelectNext : undefined;

  // Find the ID of the item to display.
  const nextItemId = idToSelectNext || allItemIds[0] || defaultId;
  if (nextItemId === defaultId && findFirstSelectedId(selection) === defaultId) {
    return undefined;
  }

  // Build the URL and redirect.
  return normalInboxesSelection([nextItemId]);
}

function trackCellSelection<T extends ItemId>(
  itemId: T,
  page: CellsPanelConversationsPageState,
  queryHash: string,
  highlightLength: number,
  allItemIds: ReadonlyArray<T>,
) {
  const {conversationMatchedQueries, conversationHighlights, conversationSearchResults} = page;
  const itemIdIndex = allItemIds.indexOf(itemId);

  const searchResult = conversationSearchResults?.[itemId];

  trackSearchConversationCellClick({
    queryHash,
    id: itemId,
    position: itemIdIndex + 1,
    matchedQueries: conversationMatchedQueries[itemId],
    highlights: searchResult?.mergedHighlights ?? conversationHighlights[itemId],
    highlightLength,
    classificationLabels: searchResult?.classificationLabels,
    isTeamConversation: searchResult && isTeamNamespace(searchResult.namespace),
  });
}

function isHiddenConversationSelected<T extends ItemId>(
  selection: InboxesSelection<T>,
  hiddenConversationId?: T | null,
): boolean {
  return (
    !isUndefined(hiddenConversationId) &&
    !isNull(hiddenConversationId) &&
    computeIsIdSelected(selection, hiddenConversationId)
  );
}

function calculateSelectedIndexes<T extends ItemId>(
  itemIndexes: ItemIndexes,
  allItemIds: ReadonlyArray<T>,
  selection: InboxesSelection<ItemId>,
) {
  if (computeIsAllExceptSelection(selection)) {
    // start with the item id's and filter based on deselected
    return allItemIds
      .filter((id) => !selection.deselectedIds.includes(id))
      .map((id) => itemIndexes[id.toString()])
      .filter((index) => Number.isInteger(index));
  }

  return _(selection.selectedIds)
    .map((id) => itemIndexes[id.toString()])
    .filter((index) => Number.isInteger(index))
    .value();
}

/*
 * New index selection.
 */

function findItemToSelectNext<T extends ItemId>(
  props: CellsItemsProps<T>,
  filteredItemIds: ReadonlyArray<T>,
  currentItemToSelectNext: T | undefined,
  wasLastKeyDirectionBefore: boolean,
  previousHiddenConversationId: T | null | undefined,
  previousIndexOfHiddenConversationIdRef: number | null,
): T | undefined {
  // We need some selected IDs to find the next one.
  const {page, itemIndexes, reselectDirection} = props;
  const {selection} = page.parameters;

  // Find the reference item.
  const referenceId = findFirstSelectedId(selection);
  if (isUndefined(referenceId)) {
    return undefined;
  }

  // If the reference item disappeared, stick with the next item we previously calculated if it is still valid.
  const referenceIndex =
    referenceId === previousHiddenConversationId && previousIndexOfHiddenConversationIdRef
      ? previousIndexOfHiddenConversationIdRef
      : itemIndexes[referenceId];
  if (isUndefined(referenceIndex)) {
    return !isUndefined(currentItemToSelectNext) && filteredItemIds.includes(currentItemToSelectNext)
      ? currentItemToSelectNext
      : undefined;
  }

  // Find the next ID to select.
  const shouldGoUp = findShouldGoUp(reselectDirection, wasLastKeyDirectionBefore);
  const newIndex = findNewIndex(filteredItemIds, selection, referenceIndex, shouldGoUp);

  // Update the next item ID.
  const newItemId = filteredItemIds[newIndex];
  return !computeIsIdSelected(selection, newItemId) ? newItemId : undefined;
}

function findShouldGoUp(reselectDirection: ReselectDirectionsEnum, wasLastKeyDirectionBefore: boolean) {
  switch (reselectDirection) {
    case ReselectDirectionsEnum.UP:
      return true;

    case ReselectDirectionsEnum.DOWN:
      return false;

    case ReselectDirectionsEnum.SMART:
    default:
      return wasLastKeyDirectionBefore;
  }
}

function findNewIndex<T extends ItemId>(
  allItemIds: ReadonlyArray<T>,
  selection: InboxesSelection<T>,
  referenceIndex: number,
  shouldGoUp: boolean,
) {
  // Find the possible values.
  const nextIndexUp = findLastIndex(allItemIds, (id) => !computeIsIdSelected(selection, id), referenceIndex);
  const nextIndexDown = findIndex(allItemIds, (id) => !computeIsIdSelected(selection, id), referenceIndex);

  // Find the new index to select.
  if (shouldGoUp && nextIndexUp >= 0) {
    return nextIndexUp;
  }

  if (nextIndexDown >= 0) {
    return nextIndexDown;
  }

  if (nextIndexUp >= 0) {
    return nextIndexUp;
  }

  return 0;
}

function filterHiddenConversationIdFromItemIds<T extends ItemId>(
  itemIds: ReadonlyArray<T>,
  hiddenConversationId?: T | null,
) {
  return _(itemIds)
    .map((idOrArray) => {
      if (!hiddenConversationId) {
        return idOrArray;
      }

      if (Array.isArray(idOrArray)) {
        return without(idOrArray, hiddenConversationId);
      }

      if (idOrArray === hiddenConversationId) {
        return null;
      }

      return idOrArray;
    })
    .compact()
    .value() as typeof itemIds;
}

function computePreviousOverridenIndex<T extends ItemId>(
  isSelectedIdFirst: boolean,
  isSelectedIdLast: boolean,
  allItemIds: ReadonlyArray<T>,
  previousIndexOfHiddenConversationId: number | undefined | null,
  isBefore: boolean,
) {
  if (isSelectedIdFirst) {
    return -1;
  }

  if (isSelectedIdLast) {
    return allItemIds.length - 1;
  }

  if (isBefore) {
    return previousIndexOfHiddenConversationId || -1;
  }

  if (previousIndexOfHiddenConversationId) {
    return previousIndexOfHiddenConversationId - 1;
  }

  return -2;
}

/*
 * Performance interaction.
 */

function maybeLogViewerFetchInteraction<T extends ItemId>(
  event: FrontClickEvent<HTMLElement>,
  selection: InboxesSelection<T>,
  itemId: T,
) {
  // Do not log if we're multi-selecting conversations.
  if (hasMetaKey(event) || event.shiftKey) {
    return;
  }

  // Do not log if we're clicking the only selected cell.
  if (findOnlySelectedId(selection) === itemId) {
    return;
  }

  const performanceId = startPerformanceInteraction(event, PerformanceInteractionTypesEnum.VIEWER_FETCH);
  if (performanceId) {
    viewerFetchPerformanceId.set(performanceId);
  }
}
